import { useEffect, useState } from 'react';

import type { TaskExecutionSummary } from '@/lib/api/generated/model';
import { formatDurationMs, runDuration } from '../executionHistory';

const TICK_MS = 1000;

/**
 * How long a run has taken so far, counting while it runs (spec 097 FR-011).
 *
 * A finished run answers with its recorded span, exactly as `runDuration`
 * always has; a live run counts up from its start once a second and stops the
 * moment it is no longer live. A start in the future — a clock that disagrees
 * with the server — reads as zero rather than a negative span.
 */
export function useLiveDuration(
  execution: Pick<TaskExecutionSummary, 'started_at' | 'ended_at'>,
  live: boolean,
): string | null {
  const started = execution.started_at
    ? Date.parse(execution.started_at)
    : Number.NaN;
  const ticking = live && Number.isFinite(started);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!ticking) return undefined;
    const id = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(id);
    };
  }, [ticking]);

  if (!ticking) return runDuration(execution);
  return formatDurationMs(Math.max(0, now - started));
}
